import { type RefObject, useRef } from 'react'
import { useHover, usePress } from 'react-aria'

import { getFacingCount } from '@/lib/utils'
import { useRegisterShelf } from '@components/storeMap/floor/ShelfRegistry'
import useMergedRef from '@hooks/useMergedRef'
import { cx } from '@utils/cx'

import type { ShelfState } from '@components/storeMap/floor/2d/Floor'
import type { Shelf as ShelfType } from '@state/shelf/types'

export interface ShelfProps {
  shelf: ShelfType
  onShelfClick?: (shelfId: string) => void
  onHover?: (shelf?: {element: RefObject<SVGRectElement>; shelf: ShelfType}) => void
  className?: string
  fill?: string
  stroke?: string
  state?: ShelfState
}

function Shelf({ shelf, className = '', fill, stroke, onShelfClick, onHover: onHoverExternal, state = 'default' }: ShelfProps) {
  const shelfRegisterHook = useRegisterShelf(shelf.id)

  const { id, name, x, y, width, depth, rotationDeg } = shelf
  const svgRectRef = useRef<SVGRectElement | null>(null)

  const isNoData = shelf.dayState === 'noData'

  const { pressProps } = usePress({ isDisabled: isNoData, onPress: () => onShelfClick?.(id) })
  const { hoverProps } = useHover({ onHoverChange: onHover })

  const assembledClassName = cx('transition-colors', className)

  const hasFacings = getFacingCount(shelf) > 0

  function onHover(hovered: boolean) {
    if (!onHoverExternal) return
    if (!svgRectRef.current) return
    if (hovered) {
      onHoverExternal({ element: svgRectRef as RefObject<SVGRectElement>, shelf })
    } else {
      onHoverExternal(undefined)
    }
  }

  const multiRef = useMergedRef(svgRectRef, shelfRegisterHook)

  const defaultFill = hasFacings ? 'var(--color-shelf)' : 'var(--color-shelf-no-facings)'
  // transparent, not none: the outline must keep its hover tooltip
  const resolvedFill = fill ?? (isNoData ? 'transparent' : defaultFill)

  const resolvedStroke = stroke ?? 'var(--color-shelf-border)'

  return (
    <rect
      ref={multiRef}
      data-state={state}
      data-day-state={shelf.dayState}
      key={`floor:shelf-${id}`}
      data-shelf-id={name}
      id={id}
      data-testid={`floor:shelf:${id}`}
      role={isNoData ? undefined : 'button'}
      x={x}
      y={y}
      className={assembledClassName}
      width={width}
      height={depth}
      transform={`rotate(${rotationDeg}, ${x}, ${y})`}
      vectorEffect="non-scaling-stroke"
      style={{ fill: resolvedFill, stroke: resolvedStroke, strokeDasharray: isNoData ? '4 3' : undefined }}
      {...pressProps}
      {...hoverProps}
    />
  )
}

export default Shelf